import React, { useEffect, useRef, useState, useCallback } from 'react';
import './FloatingOrbs.css';

const FloatingOrbs = ({ 
  containerRef,
  orbCount = 3,
  minSize = 100,
  maxSize = 250,
  cursorAttraction = 'medium' // 'low', 'medium', 'high', or number (0-1)
}) => {
  const orbsContainerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const [orbs, setOrbs] = useState([]);
  const orbsRef = useRef([]);

  // Map cursor attraction to force multiplier
  const getAttractionForce = () => {
    if (typeof cursorAttraction === 'number') {
      return cursorAttraction;
    }
    switch (cursorAttraction) {
      case 'low': return 0.05;
      case 'medium': return 0.15;
      case 'high': return 0.25;
      default: return 0.15;
    }
  };

  // Initialize orbs with random properties
  useEffect(() => {
    // Color palette - cycle through if more orbs than colors
    const colorPalette = [
      ['var(--accent-color)', 'var(--accent-color2)'],
      ['var(--accent-color2)', 'var(--light-color)'],
      ['var(--light-color)', 'var(--accent-color2)'],
      ['var(--accent-color)', 'var(--light-color)'],
      ['var(--secondary-color)', 'var(--accent-color2)']
    ];

    // Generate positions to avoid initial overlap
    const generatePositions = (count) => {
      const positions = [];
      const gridSize = Math.ceil(Math.sqrt(count));
      const cellWidth = 80 / gridSize; // Leave 10% margin on each side
      const cellHeight = 80 / gridSize;
      
      for (let i = 0; i < count; i++) {
        const row = Math.floor(i / gridSize);
        const col = i % gridSize;
        positions.push({
          x: 10 + cellWidth * col + cellWidth / 2,
          y: 10 + cellHeight * row + cellHeight / 2
        });
      }
      return positions;
    };

    const positions = generatePositions(orbCount);
    const sizeRange = maxSize - minSize;

    const newOrbs = Array.from({ length: orbCount }, (_, i) => {
      const size = minSize + Math.random() * sizeRange;
      const colorPair = colorPalette[i % colorPalette.length];
      
      return {
        id: i,
        size: size,
        x: positions[i].x + (Math.random() - 0.5) * 10, // Start with some randomness
        y: positions[i].y + (Math.random() - 0.5) * 10,
        vx: (Math.random() - 0.5) * 0.2, // Random velocity
        vy: (Math.random() - 0.5) * 0.2,
        colors: colorPair,
        scale: 1,
        targetScale: 1,
        mass: size / 150, // Mass proportional to size
        radius: size / 3 // Store radius for collision calculations
      };
    });

    setOrbs(newOrbs);
    orbsRef.current = newOrbs;
  }, [orbCount, minSize, maxSize]);

  // Calculate distance between two orbs
  const getDistance = (orb1, orb2, rect) => {
    const x1 = (orb1.x / 100) * rect.width;
    const y1 = (orb1.y / 100) * rect.height;
    const x2 = (orb2.x / 100) * rect.width;
    const y2 = (orb2.y / 100) * rect.height;
    
    const dx = x2 - x1;
    const dy = y2 - y1;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Handle collision between two orbs
  const handleOrbCollision = (orb1, orb2, rect) => {
    const distance = getDistance(orb1, orb2, rect);
    const minDistance = orb1.radius + orb2.radius;
    
    if (distance < minDistance && distance > 0) {
      // Calculate collision response
      const x1 = (orb1.x / 100) * rect.width;
      const y1 = (orb1.y / 100) * rect.height;
      const x2 = (orb2.x / 100) * rect.width;
      const y2 = (orb2.y / 100) * rect.height;
      
      const dx = x2 - x1;
      const dy = y2 - y1;
      
      // Normalize
      const nx = dx / distance;
      const ny = dy / distance;
      
      // Relative velocity
      const dvx = orb2.vx - orb1.vx;
      const dvy = orb2.vy - orb1.vy;
      
      // Relative velocity in collision normal direction
      const dvn = dvx * nx + dvy * ny;
      
      // Do not resolve if velocities are separating
      if (dvn > 0) return;
      
      // Collision impulse with some elasticity
      const elasticity = 0.2;
      const impulse = 2 * dvn / (orb1.mass + orb2.mass);
      
      // Apply impulse
      orb1.vx += impulse * orb2.mass * nx * elasticity;
      orb1.vy += impulse * orb2.mass * ny * elasticity;
      orb2.vx -= impulse * orb1.mass * nx * elasticity;
      orb2.vy -= impulse * orb1.mass * ny * elasticity;
      
      // Separate orbs to prevent overlap
      const overlap = minDistance - distance;
      const separationX = nx * overlap * 0.5;
      const separationY = ny * overlap * 0.5;
      
      orb1.x -= (separationX / rect.width) * 100;
      orb1.y -= (separationY / rect.height) * 100;
      orb2.x += (separationX / rect.width) * 100;
      orb2.y += (separationY / rect.height) * 100;
    }
  };

  // Physics update function
  const updateOrbs = useCallback(() => {
    const container = orbsContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = mousePositionRef.current.x;
    const mouseY = mousePositionRef.current.y;

    // First, handle orb-to-orb collisions
    for (let i = 0; i < orbsRef.current.length; i++) {
      for (let j = i + 1; j < orbsRef.current.length; j++) {
        handleOrbCollision(orbsRef.current[i], orbsRef.current[j], rect);
      }
    }

    // Then update each orb
    orbsRef.current = orbsRef.current.map(orb => {
      // Calculate mouse influence
      const orbCenterX = (orb.x / 100) * rect.width;
      const orbCenterY = (orb.y / 100) * rect.height;
      const dx = orbCenterX - mouseX;
      const dy = orbCenterY - mouseY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const maxDistance = 200;

      // Apply mouse attraction (changed from repulsion)
      if (distance < maxDistance && distance > 0) {
        const attractionForce = getAttractionForce();
        const force = (1 - distance / maxDistance) * attractionForce / orb.mass;
        const angle = Math.atan2(dy, dx);
        
        // Attract toward cursor (negative force)
        orb.vx -= Math.cos(angle) * force;
        orb.vy -= Math.sin(angle) * force;
        
        // Scale effect when near cursor
        orb.targetScale = 1 + (1 - distance / maxDistance) * 0.1;
      } else {
        orb.targetScale = 1;
      }

      // Smooth scale transition
      orb.scale += (orb.targetScale - orb.scale) * 0.1;

      // Add some random wandering force (reduced when orbs are close)
      const wanderForce = 0.05;
      orb.vx += (Math.random() - 0.5) * wanderForce;
      orb.vy += (Math.random() - 0.5) * wanderForce;

      // Update position
      orb.x += orb.vx;
      orb.y += orb.vy;

      // Apply drag (air resistance)
      orb.vx *= 0.9;
      orb.vy *= 0.9;

      // Bounce off edges with dampening
      const marginX = (orb.radius / rect.width) * 100; // Convert to percentage
      const marginY = (orb.radius / rect.height) * 100;
      
      if (orb.x - marginX <= 0 || orb.x + marginX >= 100) {
        orb.vx *= -0.5; // Bounce with energy loss
        orb.x = orb.x - marginX <= 0 ? marginX : 100 - marginX;
      }
      
      if (orb.y - marginY <= 0 || orb.y + marginY >= 100) {
        orb.vy *= -0.5;
        orb.y = orb.y - marginY <= 0 ? marginY : 100 - marginY;
      }

      // Limit maximum velocity
      const maxVelocity = 0.8;
      const velocity = Math.sqrt(orb.vx * orb.vx + orb.vy * orb.vy);
      if (velocity > maxVelocity) {
        orb.vx = (orb.vx / velocity) * maxVelocity;
        orb.vy = (orb.vy / velocity) * maxVelocity;
      }

      return orb;
    });

    setOrbs([...orbsRef.current]);
  }, [getAttractionForce]);

  // Mouse tracking
  useEffect(() => {
    const handleMouseMove = (e) => {
      const container = orbsContainerRef.current;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      mousePositionRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };

    // Animation loop
    const animate = () => {
      updateOrbs();
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Use containerRef if provided, otherwise use document
    const element = containerRef?.current || document;
    element.addEventListener('mousemove', handleMouseMove);
    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      element.removeEventListener('mousemove', handleMouseMove);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [updateOrbs, containerRef]);

  return (
    <div ref={orbsContainerRef} className="floating-orbs-container">
      {orbs.map((orb) => (
        <div
          key={orb.id}
          className="floating-orb-physics"
          style={{
            width: orb.size,
            height: orb.size,
            left: `${orb.x}%`,
            top: `${orb.y}%`,
            transform: `translate(-50%, -50%) scale(${orb.scale})`,
            background: `linear-gradient(135deg, ${orb.colors[0]}, ${orb.colors[1]})`,
          }}
        />
      ))}
    </div>
  );
};

export default FloatingOrbs;